import { memo, useCallback, useState } from 'react'
import { Box, Typography, Alert, Skeleton, Chip, Button } from '@mui/material'
import {
  Timeline as TimelineIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import ModularTimeline from 'src/v3/components/timeline/ModularTimeline'
import type { TimelineEventData } from 'src/v3/components/timeline/ModularTimeline'
import type {
  RosterCEActivityData,
  RosterAssignmentEventData,
  RosterUserActionData,
  RosterLicenseActivityData,
  RosterCredentialActivityData,
  RosterUserTimelineQueryParams,
  RosterUserActivityEvent,
} from '../types'
import { useUserActivityTimeline } from '../hooks/use-user-activity-timeline'
import UserTimelineFilterPanel from './user-timeline-filter-panel'
import CEActivityModal from './modals/ce-activity-modal'
import AssignmentActivityModal from './modals/assignment-activity-modal'
import UserActionModal from './modals/user-action-modal'
import LicenseActivityModal from './modals/license-activity-modal'
import CredentialActivityModal from './modals/credential-activity-modal'

interface UserActivityTimelineProps {
  userID: string
  linkedServiceID: string
}

type ModalState =
  | { type: 'ce-activity'; data: RosterCEActivityData; eventType: string }
  | { type: 'assignment'; data: RosterAssignmentEventData }
  | { type: 'user-action'; data: RosterUserActionData }
  | { type: 'license'; data: RosterLicenseActivityData }
  | { type: 'credential'; data: RosterCredentialActivityData }
  | { type: 'closed' }

export let UserActivityTimeline = memo(
  ({ userID, linkedServiceID }: UserActivityTimelineProps) => {
    let [queryParams, setQueryParams] = useState<
      RosterUserTimelineQueryParams | undefined
    >()

    let [modalState, setModalState] = useState<ModalState>({ type: 'closed' })

    let { timelineGroups, statistics, isLoading, isError, error, refetch } =
      useUserActivityTimeline({
        userID,
        linkedServiceID,
        queryParams,
        enabled: !!userID && !!linkedServiceID,
      })

    let handleFiltersChange = useCallback(
      (newQueryParams: RosterUserTimelineQueryParams) => {
        setQueryParams(newQueryParams)
      },
      [],
    )

    let handleEventClick = useCallback((eventData: TimelineEventData) => {
      let originalEvent = eventData.data as RosterUserActivityEvent

      if (!originalEvent?.data) {
        return
      }

      if ('ceActivity' in originalEvent.data) {
        setModalState({
          type: 'ce-activity',
          data: originalEvent.data.ceActivity as any,
          eventType: originalEvent.type,
        })
      } else if ('assignmentEvent' in originalEvent.data) {
        setModalState({
          type: 'assignment',
          data: originalEvent.data.assignmentEvent as any,
        })
      } else if ('userAction' in originalEvent.data) {
        setModalState({
          type: 'user-action',
          data: originalEvent.data.userAction as any,
        })
      } else if ('credentialActivity' in originalEvent.data) {
        setModalState({
          type: 'credential',
          data: originalEvent.data.credentialActivity as any,
        })
      } else if ('licenseActivity' in originalEvent.data) {
        setModalState({
          type: 'license',
          data: originalEvent.data.licenseActivity as any,
        })
      }
    }, [])

    let handleModalClose = useCallback(() => {
      setModalState({ type: 'closed' })
    }, [])

    if (isLoading) {
      return (
        <Box>
          <Box className="px-6 mb-6">
            <Box className="flex items-center justify-between mb-2">
              <Typography
                variant="h6"
                className="flex items-center gap-2 font-semibold"
                id="activity-timeline-heading"
              >
                <TimelineIcon aria-hidden="true" />
                Activity Timeline
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Complete history of user learning events, assignments, and
              credentials.
            </Typography>
          </Box>

          <UserTimelineFilterPanel onFiltersChange={handleFiltersChange} />

          <Box
            role="status"
            aria-live="polite"
            aria-label="Loading activity timeline"
          >
            <Skeleton
              variant="rectangular"
              height={600}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Box>
      )
    }

    if (isError) {
      return (
        <Box>
          <Box className="px-6 mb-6">
            <Typography
              variant="h6"
              className="flex items-center gap-2 font-semibold"
              id="activity-timeline-heading"
            >
              <TimelineIcon aria-hidden="true" />
              Activity Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete history of user learning events, assignments, and
              credentials.
            </Typography>
          </Box>

          <UserTimelineFilterPanel onFiltersChange={handleFiltersChange} />

          <Alert
            severity="error"
            action={
              <Button
                onClick={() => refetch()}
                size="small"
                color="error"
                variant="outlined"
              >
                Retry
              </Button>
            }
          >
            <Box className="flex items-center gap-2">
              <WarningIcon />
              <Box>
                <Typography variant="body2" className="font-semibold">
                  Failed to Load Activity Timeline
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {error?.message ||
                    'An error occurred while loading the timeline data'}
                </Typography>
              </Box>
            </Box>
          </Alert>
        </Box>
      )
    }

    if (timelineGroups.length === 0) {
      return (
        <Box>
          <Box className="px-6 mb-6">
            <Box className="flex items-center justify-between mb-2">
              <Typography
                variant="h6"
                className="flex items-center gap-2 font-semibold"
                id="activity-timeline-heading"
              >
                <TimelineIcon aria-hidden="true" />
                Activity Timeline
              </Typography>
              {statistics.filteredCount !== statistics.totalCount && (
                <Chip
                  size="medium"
                  label={`0 of ${statistics.totalCount} events shown`}
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Complete history of user learning events, assignments, and
              credentials.
              {statistics.filteredCount !== statistics.totalCount && (
                <Box component="span" className="ml-2 font-medium">
                  All events are filtered out. Try adjusting your filters.
                </Box>
              )}
            </Typography>
          </Box>

          <UserTimelineFilterPanel onFiltersChange={handleFiltersChange} />

          <Box
            className="grid py-12 place-items-center"
            role="status"
            aria-labelledby="activity-timeline-heading"
          >
            <Box className="text-center" sx={{ color: 'text.secondary' }}>
              <TimelineIcon
                className="mb-4 opacity-50"
                sx={{ fontSize: 64 }}
                aria-hidden="true"
              />
              <Typography variant="h6" className="mb-2">
                {statistics.filteredCount !== statistics.totalCount
                  ? 'No Activities Match Filters'
                  : 'No Activity Found'}
              </Typography>
              <Typography variant="body2">
                {statistics.filteredCount !== statistics.totalCount
                  ? 'Try adjusting your filters to see more events.'
                  : 'No learning events found for the selected date range.'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )
    }

    return (
      <Box>
        <Box className="px-6 mb-6">
          <Box className="flex items-center justify-between mb-2">
            <Typography
              variant="h6"
              className="flex items-center gap-2 font-semibold"
              id="activity-timeline-heading"
            >
              <TimelineIcon aria-hidden="true" />
              Activity Timeline
            </Typography>
            <Box className="flex items-center gap-2">
              <Chip
                size="medium"
                label={`${statistics.totalEvents} events`}
                color="primary"
              />
              {statistics.filteredCount !== statistics.totalCount && (
                <Chip
                  size="medium"
                  label={`${statistics.filteredCount} of ${statistics.totalCount} shown`}
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Complete history of user learning events, assignments, and
            credentials. Click any event for detailed information.
            {statistics.filteredCount !== statistics.totalCount && (
              <Box component="span" className="ml-2 font-medium">
                Filters are active - showing {statistics.filteredCount} of{' '}
                {statistics.totalCount} events.
              </Box>
            )}
          </Typography>
        </Box>

        <UserTimelineFilterPanel onFiltersChange={handleFiltersChange} />

        <Box
          className="w-full overflow-auto"
          sx={{ height: 600 }}
          role="region"
          aria-labelledby="activity-timeline-heading"
        >
          <ModularTimeline
            groups={timelineGroups}
            isLoading={false}
            onEventClick={handleEventClick}
            emptyStateTitle="No Events Found"
            emptyStateDescription="No events found for the selected date range"
            maxHeight={600}
            showStats={false}
            showTitle={false}
          />
        </Box>

        <CEActivityModal
          event={modalState.type === 'ce-activity' ? modalState.data : null}
          eventType={
            modalState.type === 'ce-activity' ? modalState.eventType : null
          }
          isOpen={modalState.type === 'ce-activity'}
          onClose={handleModalClose}
          linkedServiceID={linkedServiceID}
        />
        <AssignmentActivityModal
          event={modalState.type === 'assignment' ? modalState.data : null}
          isOpen={modalState.type === 'assignment'}
          onClose={handleModalClose}
          linkedServiceID={linkedServiceID}
          userID={userID}
        />
        <UserActionModal
          event={modalState.type === 'user-action' ? modalState.data : null}
          isOpen={modalState.type === 'user-action'}
          onClose={handleModalClose}
          linkedServiceID={linkedServiceID}
        />
        <LicenseActivityModal
          event={modalState.type === 'license' ? modalState.data : null}
          isOpen={modalState.type === 'license'}
          onClose={handleModalClose}
          linkedServiceID={linkedServiceID}
        />
        <CredentialActivityModal
          event={modalState.type === 'credential' ? modalState.data : null}
          isOpen={modalState.type === 'credential'}
          onClose={handleModalClose}
          linkedServiceID={linkedServiceID}
        />
      </Box>
    )
  },
)

export default UserActivityTimeline
